// src/data/gallery.ts
import type { Language } from "@/lib/i18n";

interface GalleryHomeItem {
  id: number;
  src: string;
  alt: string;
  className: string;
}

interface GalleryPageItem {
  id: number;
  category: string;
  title: string;
  src: string;
  className: string;
}

// Home items with i18n alt text
const galleryHomeByLang: Record<Language, GalleryHomeItem[]> = {
  id: [
    {
      id: 1,
      src: "/images/gallery/exterior-1.webp",
      alt: "Eksterior KinKen & Lanskap Tropis",
      className: "md:col-span-2 md:row-span-2 h-[300px] md:h-full",
    },
    {
      id: 2,
      src: "/images/gallery/interior-1.webp",
      alt: "Interior Executive Loft - Desain Modern Bali",
      className: "md:col-span-1 md:row-span-1 h-[300px] md:h-full",
    },
    {
      id: 3,
      src: "/images/gallery/facility-1.webp",
      alt: "Rooftop Lounge KinKen - Pemandangan Sunset Denpasar",
      className: "md:col-span-1 md:row-span-1 h-[300px] md:h-full",
    },
    {
      id: 4,
      src: "/images/gallery/facility-2.webp",
      alt: "Executive Lounge & Co-Working Space",
      className: "md:col-span-1 md:row-span-1 h-[300px] md:h-full",
    },
    {
      id: 5,
      src: "/images/gallery/room-1.webp",
      alt: "Kamar Tidur Penthouse Signature - Emperor Bed",
      className: "md:col-span-1 md:row-span-1 h-[300px] md:h-full",
    },
  ],
  en: [
    {
      id: 1,
      src: "/images/gallery/exterior-1.webp",
      alt: "KinKen Exterior & Tropical Landscape",
      className: "md:col-span-2 md:row-span-2 h-[300px] md:h-full",
    },
    {
      id: 2,
      src: "/images/gallery/interior-1.webp",
      alt: "Executive Loft Interior - Modern Bali Design",
      className: "md:col-span-1 md:row-span-1 h-[300px] md:h-full",
    },
    {
      id: 3,
      src: "/images/gallery/facility-1.webp",
      alt: "KinKen Rooftop Lounge - Sunset View Denpasar",
      className: "md:col-span-1 md:row-span-1 h-[300px] md:h-full",
    },
    {
      id: 4,
      src: "/images/gallery/facility-2.webp",
      alt: "Executive Lounge & Co-Working Space",
      className: "md:col-span-1 md:row-span-1 h-[300px] md:h-full",
    },
    {
      id: 5,
      src: "/images/gallery/room-1.webp",
      alt: "Penthouse Signature Bedroom - Emperor Bed",
      className: "md:col-span-1 md:row-span-1 h-[300px] md:h-full",
    },
  ],
};

// Page items with i18n
const galleryPageByLang: Record<Language, GalleryPageItem[]> = {
  id: [
    {
      id: 1,
      category: "Interior",
      title: "Studio Deluxe - Desain Modern Minimalis",
      src: "/images/gallery/interior-1.webp",
      className: "col-span-1 md:col-span-2 md:row-span-2",
    },
    {
      id: 2,
      category: "Eksterior",
      title: "Pintu Masuk & Area Lobby KinKen",
      src: "/images/gallery/exterior-1.webp",
      className: "col-span-1 md:col-span-1 md:row-span-1",
    },
    {
      id: 3,
      category: "Fasilitas",
      title: "Wellness Pavilion - Peralatan Gym Premium",
      src: "/images/gallery/facility-1.webp",
      className: "col-span-1 md:col-span-1 md:row-span-1",
    },
    {
      id: 4,
      category: "Fasilitas",
      title: "Executive Lounge - Private Work Booths",
      src: "/images/gallery/facility-2.webp",
      className: "col-span-1 md:col-span-1 md:row-span-2",
    },
    {
      id: 5,
      category: "Kamar",
      title: "Executive Loft - Super King Bed Setup",
      src: "/images/gallery/room-1.webp",
      className: "col-span-1 md:col-span-1 md:row-span-1",
    },
    {
      id: 6,
      category: "Fasilitas",
      title: "Dapur Gourmet - Peralatan Chef-Grade",
      src: "/images/gallery/facility-3.webp",
      className: "col-span-1 md:col-span-2 md:row-span-1",
    },
    {
      id: 7,
      category: "Eksterior",
      title: "KinKen di Malam Hari - Arsitektur Menyala",
      src: "/images/gallery/exterior-2.webp",
      className: "col-span-1 md:col-span-1 md:row-span-1",
    },
    {
      id: 8,
      category: "Kamar",
      title: "Penthouse Signature - Freestanding Bathtub",
      src: "/images/gallery/room-2.webp",
      className: "col-span-1 md:col-span-1 md:row-span-1",
    },
    {
      id: 9,
      category: "Fasilitas",
      title: "Rooftop Lounge - Pemandangan Kota Panoramik",
      src: "/images/gallery/facility-1.webp",
      className: "col-span-1 md:col-span-1 md:row-span-1",
    },
    {
      id: 10,
      category: "Interior",
      title: "Studio Deluxe - Work Station Ergonomis",
      src: "/images/gallery/interior-1.webp",
      className: "col-span-1 md:col-span-1 md:row-span-1",
    },
    {
      id: 11,
      category: "Fasilitas",
      title: "Meditation Garden - Sanctuary Tropis",
      src: "/images/gallery/facility-3.webp",
      className: "col-span-1 md:col-span-1 md:row-span-1",
    },
    {
      id: 12,
      category: "Kamar",
      title: "Executive Loft - Pemandangan Balkon Pribadi",
      src: "/images/gallery/room-3.webp",
      className: "col-span-1 md:col-span-1 md:row-span-1",
    },
  ],
  en: [
    {
      id: 1,
      category: "Interior",
      title: "Studio Deluxe - Modern Minimalist Design",
      src: "/images/gallery/interior-1.webp",
      className: "col-span-1 md:col-span-2 md:row-span-2",
    },
    {
      id: 2,
      category: "Exterior",
      title: "KinKen Entrance & Lobby Area",
      src: "/images/gallery/exterior-1.webp",
      className: "col-span-1 md:col-span-1 md:row-span-1",
    },
    {
      id: 3,
      category: "Facilities",
      title: "Wellness Pavilion - Premium Gym Equipment",
      src: "/images/gallery/facility-1.webp",
      className: "col-span-1 md:col-span-1 md:row-span-1",
    },
    {
      id: 4,
      category: "Facilities",
      title: "Executive Lounge - Private Work Booths",
      src: "/images/gallery/facility-2.webp",
      className: "col-span-1 md:col-span-1 md:row-span-2",
    },
    {
      id: 5,
      category: "Rooms",
      title: "Executive Loft - Super King Bed Setup",
      src: "/images/gallery/room-1.webp",
      className: "col-span-1 md:col-span-1 md:row-span-1",
    },
    {
      id: 6,
      category: "Facilities",
      title: "Gourmet Kitchen - Chef-Grade Appliances",
      src: "/images/gallery/facility-3.webp",
      className: "col-span-1 md:col-span-2 md:row-span-1",
    },
    {
      id: 7,
      category: "Exterior",
      title: "KinKen at Night - Illuminated Architecture",
      src: "/images/gallery/exterior-2.webp",
      className: "col-span-1 md:col-span-1 md:row-span-1",
    },
    {
      id: 8,
      category: "Rooms",
      title: "Penthouse Signature - Freestanding Bathtub",
      src: "/images/gallery/room-2.webp",
      className: "col-span-1 md:col-span-1 md:row-span-1",
    },
    {
      id: 9,
      category: "Facilities",
      title: "Rooftop Lounge - Panoramic City View",
      src: "/images/gallery/facility-1.webp",
      className: "col-span-1 md:col-span-1 md:row-span-1",
    },
    {
      id: 10,
      category: "Interior",
      title: "Studio Deluxe - Ergonomic Work Station",
      src: "/images/gallery/interior-1.webp",
      className: "col-span-1 md:col-span-1 md:row-span-1",
    },
    {
      id: 11,
      category: "Facilities",
      title: "Meditation Garden - Tropical Sanctuary",
      src: "/images/gallery/facility-3.webp",
      className: "col-span-1 md:col-span-1 md:row-span-1",
    },
    {
      id: 12,
      category: "Rooms",
      title: "Executive Loft - Private Balcony View",
      src: "/images/gallery/room-3.webp",
      className: "col-span-1 md:col-span-1 md:row-span-1",
    },
  ],
};

// Backward compatibility
export const GALLERY_HOME_ITEMS = galleryHomeByLang.id;
export const GALLERY_PAGE_ITEMS = galleryPageByLang.id;

// i18n functions
export function getGalleryHomeItems(lang: Language = "id"): GalleryHomeItem[] {
  return galleryHomeByLang[lang] || galleryHomeByLang.id;
}

export function getGalleryPageItems(lang: Language = "id"): GalleryPageItem[] {
  return galleryPageByLang[lang] || galleryPageByLang.id;
}
